import React, { useState, useCallback, useEffect } from 'react';
import { Category } from './Category';
import {Button } from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import {Grid3x3, GridView, List, ListAlt} from "@mui/icons-material"
export default function PartSearch({ model, firmanavn, brand, mainGroupsPartGroupsAndParts,
    seoContent, heading, viewMode = 'grouped', showModeSwitcher = false, showUnavailablePartsCheckbox = false
}) {
    const [searchMode, setSearchMode] = useState(false);

    const [paddingBottom, setPaddingBottom] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const normalizeMode = (m) => (m === 'list' ? 'list' : 'grouped');
    const [mode, setMode] = useState(normalizeMode(viewMode));
    useEffect(() => {
        setMode(normalizeMode(viewMode));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewMode]);
    const [showUnavailableParts, setShowUnavailableParts] = useState(false);
    const [searchOriginMode, setSearchOriginMode] = useState(null); // 'grouped' | 'list' | null

    const sortPartGroups = (partGroups, includeUnavailable) => {
        return Object.entries(partGroups).map(([key, value]) => {
            // Filter each part group's parts based on availability
            const filteredPartGroups = value.map(pg => ({
                ...pg,
                Parts: includeUnavailable ? pg.Parts : pg.Parts.filter(p => p.Available !== false)
            })).filter(pg => pg.Parts.length > 0);
            return [key, filteredPartGroups];
        }).filter(([, value]) => value.length > 0)
            .sort((a, b) => {
                const aValue = parseInt(a[0].split(',')[1].slice(0, -1));
                const bValue = parseInt(b[0].split(',')[1].slice(0, -1));
                return aValue - bValue;
            });
    };

    // Data for grouped view
    const [groupedPartGroupsArray, setGroupedPartGroupsArray] = useState(sortPartGroups(mainGroupsPartGroupsAndParts, showUnavailableParts));
    // Data for list view: { [letter]: Array<{name, url, group?}> }
    const [listGroups, setListGroups] = useState({});
    const [chosenCategory, setChosenCategory] = useState('');
    const [chosenSubCategory, setChosenSubCategory] = useState('');

    const getPartPartGroupUrl = useCallback((partname, partgroupname) => {
        const queryParams = new URLSearchParams({
            firmanavn,
            partgroup: partgroupname,
            part: partname,
            brand,
            Models: model,
            start: "1"
        });
        const baseUrl = document.location.href.split(/bildeler|Bildeler/)[0];
        return `${baseUrl}Søk?${queryParams.toString()}`;
    }, [firmanavn, brand, model]);

    const buildAllPartsListGroups = useCallback(() => {
        const allParts = [];
        Object.entries(mainGroupsPartGroupsAndParts).forEach(([, partGroups]) => {
            partGroups.forEach(partGroup => {
                partGroup.Parts.forEach(part => {
                    if (showUnavailableParts || part.Available !== false) {
                        allParts.push({ name: part.Name, url: part.Url, group: partGroup.Name, available: part.Available !== false });
                    }
                });
            });
        });
        // Deduplicate by name (prefer available items when both exist)
        const map = new Map();
        for (const p of allParts) {
            const k = p.name.toLowerCase();
            const existing = map.get(k);
            if (!existing || (p.available && !existing.available)) {
                map.set(k, p);
            }
        }
        const deduped = Array.from(map.values());
        return deduped.sort((a, b) => a.name.localeCompare(b.name)).reduce((acc, part) => {
            const firstLetter = part.name.charAt(0).toUpperCase();
            if (!acc[firstLetter]) acc[firstLetter] = [];
            acc[firstLetter].push(part);
            return acc;
        }, {});
    }, [mainGroupsPartGroupsAndParts, showUnavailableParts]);

    const buildFilteredListGroups = useCallback((query) => {
        const q = (query || '').toLowerCase();
        if (!q) return {};
        const filteredParts = [];
        Object.entries(mainGroupsPartGroupsAndParts).forEach(([, partGroups]) => {
            partGroups.forEach(partGroup => {
                partGroup.Parts.forEach(part => {
                    if ((showUnavailableParts || part.Available !== false) && part.Name.toLowerCase().includes(q)) {
                        filteredParts.push({ name: part.Name, url: part.Url, group: partGroup.Name, available: part.Available !== false });
                    }
                });
            });
        });
        // Deduplicate by name (prefer available items when both exist)
        const map = new Map();
        for (const p of filteredParts) {
            const k = p.name.toLowerCase();
            const existing = map.get(k);
            if (!existing || (p.available && !existing.available)) {
                map.set(k, p);
            }
        }
        const deduped = Array.from(map.values());
        return deduped.sort((a, b) => a.name.localeCompare(b.name)).reduce((acc, part) => {
            const firstLetter = part.name.charAt(0).toUpperCase();
            if (!acc[firstLetter]) acc[firstLetter] = [];
            acc[firstLetter].push(part);
            return acc;
        }, {});
    }, [mainGroupsPartGroupsAndParts, showUnavailableParts]);

    const toggleSearch = (active) => {
    // 1. Check if browser supports the API
    if (!document.startViewTransition) {
      setSearchMode(active);
      return;
    }

    // 2. Trigger the smooth "snapshot" transition
    document.startViewTransition(() => {
      // React will re-render the DOM inside this callback
      setSearchMode(active);
    });
  };
  
    const searchPartGroups = useCallback((e) => {
        const query = e.target.value;
        setSearchQuery(query);
        if (query && query.trim().length > 0) {
            if (searchOriginMode == null) setSearchOriginMode(mode);
            if (mode !== 'list') setMode('list');
            setListGroups(buildFilteredListGroups(query));
        } else {
            // With empty query, show list of all parts (availability respected by checkbox)
            setListGroups(buildAllPartsListGroups());
            if (searchOriginMode === 'grouped') setMode('grouped');
            setSearchOriginMode(null);
        }
    }, [buildFilteredListGroups, buildAllPartsListGroups, showUnavailableParts, mode, searchOriginMode]);

    // Recompute when toggling showAllParts or the dataset changes
    useEffect(() => {
        setGroupedPartGroupsArray(sortPartGroups(mainGroupsPartGroupsAndParts, showUnavailableParts));
        if (mode === 'list') {
            const hasQuery = searchQuery && searchQuery.trim().length > 0;
            setListGroups(hasQuery ? buildFilteredListGroups(searchQuery) : buildAllPartsListGroups());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mainGroupsPartGroupsAndParts, showUnavailableParts]);

    useEffect(() => {
        if (mode !== 'list') return;
        const hasQuery = searchQuery && searchQuery.trim().length > 0;
        setListGroups(hasQuery ? buildFilteredListGroups(searchQuery) : buildAllPartsListGroups());
    }, [mode, showUnavailableParts, searchQuery, buildAllPartsListGroups, buildFilteredListGroups]);

    // No special handling needed on mode change; availability checkbox applies to both modes
    useEffect(() => {}, [mode]);

    return (
        <div className={`accordionContent ${searchMode ? 'textSearchActive' : ''}`} id="partSearchAccordionContent">
            <div id="partSearchColumnLeft" className="columnLeft">
                <div className="searchInputContainer">

                  
                    <input
                        placeholder="Søk i deleliste"
                        className="searchBrandByLetterInput"
                        onChange={searchPartGroups}
                        onFocus={() => setSearchMode(true)}

                    />
                    <img src="/images/search.png" alt="Search" className='searchImage' />
                </div>
                <h1>{heading}</h1>
                {seoContent?.Value &&
                    <div className="seoContent" dangerouslySetInnerHTML={{ __html: seoContent?.Value }}></div>
                }
            </div>
            

            <div className='rightArea' style={{display: 'flex', flexDirection:'column', width: '100%'}}>
            {/* Mode switcher and options */}
            {showModeSwitcher && (
                <div style={{ alignSelf: 'center', display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
                    <Button
                      variant={mode === 'grouped' ? 'contained' : 'outlined'} size="large"
                        type="button"
                        onClick={() => setMode('grouped')}
                        className={mode === 'grouped' ? '' : ''}
                        endIcon={<GridView />}
                    >
                        Vis Kategorier
                    </Button>
                    <Button
                    variant={mode === 'list' ? 'contained' : 'outlined'}
                        type="button" size="large"
                        onClick={() => setMode('list')}
                        className={mode === 'list' ? '' : ''}
                        endIcon={<ListAlt />}
                    >
                        Vis alfabetisk liste
                    </Button>
                </div>
            )}
            {showUnavailablePartsCheckbox && mode === 'list' && (
                <label style={{ alignSelf: 'center', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                    <input
                        type="checkbox"
                        checked={showUnavailableParts}
                        onChange={(e) => setShowUnavailableParts(e.target.checked)}
                    />
                    Vis også deler som ikke er på lager
                </label>
            )}

            {mode === 'list' ? (
                <ul className="searchResultsList">
                    {Object.keys(listGroups).length === 0 ? (
                        <li className="noResults">Ingen treff.</li>
                    ) : (
                        Object.entries(listGroups).map(([letter, parts]) => (
                            <li  key={letter}>
                                <h2>{letter}</h2>
                                <ul className='searchResultsListLetterHits'>
                                    {parts
                                        .sort((a, b) => a.name.localeCompare(b.name))
                                        .map((part) => {
                                            const cls = part.available ? 'partListFilteredItem' : 'partListFilteredItem partListFilteredItemUnavailable';
                                            return (
                                                <li key={part.name}>
                                                    <a className={cls} href={part.url}>{part.name}</a>
                                                </li>
                                            );
                                        })}
                                </ul>
                            </li>
                        ))
                    )}
                </ul>
            ) : (
                <ul className="partsGroupListL1" style={{ paddingBottom: paddingBottom + 72 }}>
                    {groupedPartGroupsArray.length === 0 ? (
                        <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: '3em', marginBottom: '4em' }}>
                            Ingen treff.
                        </div>
                    ) : (
                        groupedPartGroupsArray.map(([key, value]) => {
                            const parts = key.split(',');
                            const category = parts.slice(0, -2).join(',').substring(1);
                            const imageUrl = parts[parts.length - 1].slice(0, -1);
                            return (
                                <Category
                                    key={key}
                                    category={category}
                                    imageUrl={imageUrl}
                                    value={value}
                                    chosenCategory={chosenCategory}
                                    setChosenCategory={setChosenCategory}
                                    chosenSubCategory={chosenSubCategory}
                                    setChosenSubCategory={setChosenSubCategory}
                                    getPartPartGroupUrl={getPartPartGroupUrl}
                                    setPaddingBottom={setPaddingBottom}
                                />
                            );
                        })
                    )}
                </ul>
            )}
        </div></div>
    );
}
